import type {
  DealFixedRateLoanData,
  DealFloatingRateLoanData,
  DealForwardData,
  DealOptionData,
  DealRepoData,
  OTCDealData,
} from '@src/types/OTC';
import type {
  FixedRateLoanDealTradeForm,
  FloatingRateLoanDealTradeForm,
  ForwardDealTradeForm,
  ManualDealTradeForm,
  OptionDealTradeForm,
  RepoDealTradeForm,
} from '@src/types/OTC/otcManualDealFormTypes';
import type {
  DealProductBuilderRow,
  FixedRateLoanDealProductBuilderRow,
  FloatingRateLoanDealProductBuilderRow,
  ForwardDealProductBuilderRow,
  OptionDealProductBuilderRow,
  RepoDealProductBuilderRow,
} from '@src/types/OTC/otcProductBuilderTypes';
import type { WritableDraft } from 'immer';
import type {
  OTCFixedRateLoanFieldsData,
  OTCFloatingRateLoanFieldsData,
  OTCForwardFieldsData,
  OTCOptionFieldsData,
  OTCRepoFieldsData,
} from './otcProductBuilderTypes';

export const isForwardProductRow = (row: DealProductBuilderRow): row is ForwardDealProductBuilderRow => {
  return row.product === 'forward';
};

export const isFixedRateLoanProductRow = (row: DealProductBuilderRow): row is FixedRateLoanDealProductBuilderRow => {
  return row.product === 'fixedRateLoan';
};

export const isOptionProductRow = (row: DealProductBuilderRow): row is OptionDealProductBuilderRow => {
  return row.product === 'option';
};

export const isFloatingRateLoanProductRow = (
  row: DealProductBuilderRow
): row is FloatingRateLoanDealProductBuilderRow => {
  return row.product === 'floatingRateLoan';
};

export const isForwardProductFields = (data: unknown): data is OTCForwardFieldsData => {
  return typeof data === 'object' && data !== null && 'productType' in data && data.productType === 'forward';
};

export const isFixedRateLoanProductFields = (data: unknown): data is OTCFixedRateLoanFieldsData => {
  return typeof data === 'object' && data !== null && 'productType' in data && data.productType === 'fixedRateLoan';
};

export const isOptionProductFields = (data: unknown): data is OTCOptionFieldsData => {
  return typeof data === 'object' && data !== null && 'productType' in data && data.productType === 'option';
};

export const isFloatingRateLoanProductFields = (data: unknown): data is OTCFloatingRateLoanFieldsData => {
  return typeof data === 'object' && data !== null && 'productType' in data && data.productType === 'floatingRateLoan';
};

export const isRepoProductRow = (row: DealProductBuilderRow): row is RepoDealProductBuilderRow => {
  return row.product === 'repo';
};

export const isRepoProductFields = (data: unknown): data is OTCRepoFieldsData => {
  return typeof data === 'object' && data !== null && 'productType' in data && data.productType === 'repo';
};

export const isForwardDeal = (deal: OTCDealData): deal is DealForwardData => {
  return deal.SubProductType === 'OTCForward';
};

export const isFixedRateLoanDeal = (deal: OTCDealData): deal is DealFixedRateLoanData => {
  return deal.SubProductType === 'OTCFixedRateLoan';
};

export const isOptionDeal = (deal: OTCDealData): deal is DealOptionData => {
  return deal.SubProductType === 'OTCVanillaOption';
};

export const isFloatingRateLoanDeal = (deal: OTCDealData): deal is DealFloatingRateLoanData => {
  return deal.SubProductType === 'OTCFloatingRateLoan';
};

export const isRepoDeal = (deal: OTCDealData): deal is DealRepoData => {
  return deal.SubProductType === 'OTCRepo';
};

export const isForwardDealFormDraft = (
  form: WritableDraft<ManualDealTradeForm>
): form is WritableDraft<ForwardDealTradeForm> => {
  return form.bookingTypeField.value === 'forward';
};

export const isFixedRateLoanDealFormDraft = (
  form: WritableDraft<ManualDealTradeForm>
): form is WritableDraft<FixedRateLoanDealTradeForm> => {
  return form.bookingTypeField.value === 'fixedRateLoan';
};

export const isForwardDealForm = (form: ManualDealTradeForm): form is ForwardDealTradeForm => {
  return form.bookingTypeField.value === 'forward';
};

export const isFixedRateLoanDealForm = (form: ManualDealTradeForm): form is FixedRateLoanDealTradeForm => {
  return form.bookingTypeField.value === 'fixedRateLoan';
};

export const isOptionDealFormDraft = (
  form: WritableDraft<ManualDealTradeForm>
): form is WritableDraft<OptionDealTradeForm> => {
  return form.bookingTypeField.value === 'option';
};

export const isFloatingRateLoanDealFormDraft = (
  form: WritableDraft<ManualDealTradeForm>
): form is WritableDraft<FloatingRateLoanDealTradeForm> => {
  return form.bookingTypeField.value === 'floatingRateLoan';
};

export const isOptionDealForm = (form: ManualDealTradeForm): form is OptionDealTradeForm => {
  return form.bookingTypeField.value === 'option';
};

export const isFloatingRateLoanDealForm = (form: ManualDealTradeForm): form is FloatingRateLoanDealTradeForm => {
  return form.bookingTypeField.value === 'floatingRateLoan';
};

export const isRepoDealFormDraft = (
  form: WritableDraft<ManualDealTradeForm>
): form is WritableDraft<RepoDealTradeForm> => {
  return form.bookingTypeField.value === 'repo';
};

export const isRepoDealForm = (form: ManualDealTradeForm): form is RepoDealTradeForm => {
  return form.bookingTypeField.value === 'repo';
};
